import React, { useEffect, useRef } from 'react';
import Header from './Header.jsx';
import Footer from './Footer.jsx';
import { FiCheckCircle, FiTrendingUp, FiShield, FiSettings } from 'react-icons/fi';
import '../css/industriesServed.css';

// Import industry images
import steelplant from '../assets/industries/steelplant.jpg';
import miningplant from '../assets/industries/miningplant.jpg';
import marineindusties from '../assets/industries/marineindusties.jpg';
import foodindustry from '../assets/industries/foodindustry.jpg';
import constructionindustry from '../assets/industries/constructionindustry.jpg';
import refineries from '../assets/industries/refineries.jpg';

const IndustriesServed = () => {
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-animate');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const sections = sectionsRef.current;
    sections.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      sections.forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, []);

  const industries = [
    {
      title: 'Steel Plants',
      description: 'Heavy-duty valve solutions for steel manufacturing processes, handling extreme temperatures and pressures.',
      image: steelplant,
      features: [
        'High-temperature resistant valves',
        'Pressure-rated for blast furnace operations',
        'Corrosion-resistant materials',
        'Reliable performance in harsh conditions'
      ],
      applications: [
        'Blast furnace operations',
        'Continuous casting systems',
        'Steel rolling mills',
        'Heat treatment facilities'
      ]
    },
    {
      title: 'Mining Plants',
      description: 'Robust valve systems designed for harsh mining environments and abrasive material handling.',
      image: miningplant,
      features: [
        'Wear-resistant valve designs',
        'Abrasive material handling',
        'High-pressure capabilities',
        'Durable construction materials'
      ],
      applications: [
        'Ore processing plants',
        'Slurry handling systems',
        'Mine dewatering',
        'Material transport pipelines'
      ]
    },
    {
      title: 'Marine Applications',
      description: 'Corrosion-resistant valves engineered for marine environments and offshore operations.',
      image: marineindusties,
      features: [
        'Marine-grade corrosion resistance',
        'Saltwater environment compatibility',
        'Reliable sealing in harsh conditions',
        'Compliance with marine standards'
      ],
      applications: [
        'Shipboard systems',
        'Offshore platforms',
        'Port facilities',
        'Marine engineering projects'
      ]
    },
    {
      title: 'Food Processing Industry',
      description: 'Sanitary valve solutions meeting strict hygiene standards for food and beverage production.',
      image: foodindustry,
      features: [
        'Sanitary design standards',
        'Easy cleaning and maintenance',
        'Food-grade materials',
        'Hygiene compliance'
      ],
      applications: [
        'Food processing plants',
        'Beverage production',
        'Dairy processing',
        'Pharmaceutical manufacturing'
      ]
    },
    {
      title: 'Construction Industry',
      description: 'Reliable valve systems for construction equipment and infrastructure development projects.',
      image: constructionindustry,
      features: [
        'Durable construction',
        'Easy installation',
        'Cost-effective solutions',
        'Wide range of applications'
      ],
      applications: [
        'Construction equipment',
        'Infrastructure projects',
        'Water supply systems',
        'Building services'
      ]
    },
    {
      title: 'Refineries',
      description: 'High-performance valves for petroleum refining processes and chemical processing plants.',
      image: refineries,
      features: [
        'High-pressure ratings',
        'Chemical resistance',
        'Temperature tolerance',
        'API standards compliance'
      ],
      applications: [
        'Crude oil processing',
        'Petrochemical plants',
        'Refinery operations',
        'Chemical processing'
      ]
    }
  ];

  return (
    <>
      <Header />
      
      {/* Hero Banner Section */}
      <section className="is-hero">
        <div className="is-hero-background">
          <img
            src="/industries_banner.jpg"
            alt="Industries We Serve Banner"
            className="is-hero-image"
          />
          <div className="is-hero-overlay"></div>
        </div>
        <div className="is-hero-content">
          <h1 className="is-hero-title">Industries We Serve</h1>
          <p className="is-hero-subtitle">
            Delivering precision valve solutions across diverse industrial sectors with unmatched reliability and performance
          </p>
        </div>
      </section>

      {/* Industries Grid Section */}
      <section className="is-section">
        <div className="is-container">
          <div className="is-intro">
            <h2 className="is-section-title">Our Industrial Expertise</h2>
            <p className="is-section-description">
              ANU Flow Control provides comprehensive valve solutions tailored to meet the unique requirements 
              of various industries. Our products are engineered to deliver exceptional performance, reliability, 
              and durability in the most demanding industrial environments.
            </p>
          </div>

          <div className="is-industries-grid">
            {industries.map((industry, index) => (
              <div 
                key={index}
                ref={(el) => (sectionsRef.current[index] = el)}
                className="is-industry-card"
              >
                <div className="is-industry-image-wrapper">
                  <img 
                    src={industry.image} 
                    alt={industry.title}
                    className="is-industry-image"
                  />
                  <div className="is-industry-overlay"></div>
                </div>
                <div className="is-industry-content">
                  <h3 className="is-industry-title">{industry.title}</h3>
                  <p className="is-industry-description">{industry.description}</p>
                  
                  <div className="is-industry-features">
                    <h4 className="is-features-title">Key Features</h4>
                    <ul className="is-features-list">
                      {industry.features.map((feature, idx) => (
                        <li key={idx} className="is-feature-item">
                          <FiCheckCircle className="is-feature-icon" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="is-industry-applications">
                    <h4 className="is-applications-title">Applications</h4>
                    <ul className="is-applications-list">
                      {industry.applications.map((application, idx) => (
                        <li key={idx} className="is-application-item">
                          <FiTrendingUp className="is-application-icon" />
                          <span>{application}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="is-why-choose">
        <div className="is-container">
          <h2 className="is-section-title">Why Industries Choose ANU Flow Control</h2>
          <div className="is-benefits-grid">
            <div className="is-benefit-card">
              <div className="is-benefit-icon">
                <FiShield />
              </div>
              <h3 className="is-benefit-title">Reliability</h3>
              <p className="is-benefit-description">
                Proven performance in critical industrial applications with consistent quality and durability.
              </p>
            </div>
            <div className="is-benefit-card">
              <div className="is-benefit-icon">
                <FiSettings />
              </div>
              <h3 className="is-benefit-title">Custom Solutions</h3>
              <p className="is-benefit-description">
                Tailored valve solutions designed to meet specific industry requirements and operational needs.
              </p>
            </div>
            <div className="is-benefit-card">
              <div className="is-benefit-icon">
                <FiTrendingUp />
              </div>
              <h3 className="is-benefit-title">Industry Expertise</h3>
              <p className="is-benefit-description">
                Deep understanding of industrial processes and requirements across diverse sectors.
              </p>
            </div>
            <div className="is-benefit-card">
              <div className="is-benefit-icon">
                <FiCheckCircle />
              </div>
              <h3 className="is-benefit-title">Quality Assurance</h3>
              <p className="is-benefit-description">
                Stringent quality control and compliance with international standards and certifications.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default IndustriesServed;

